/**
 * #1292 - Notification post-login redirect.
 *
 * The backend notification link lands the user on the login page with url params saying where
 * to go (?view=SUBJECT&projectId=..&siteId=..&subjectId=..). We decode those params into one of
 * our own routes here - the backend doesn't know the portal's URL structure - and remember the
 * target through the login flow.
 */

const KEY = 'postLoginRedirect'

// How each `view` maps to a portal route. The backend only sends ids; the portal owns its URLs.
const PATH_BY_VIEW: Record<string, (params: URLSearchParams) => string | null> = {
  SUBJECT: (params) => {
    const projectId = params.get('projectId')
    const siteId = params.get('siteId')
    const subjectId = params.get('subjectId')
    if (!projectId || !siteId || !subjectId) return null
    return `/project/${projectId}/site/${siteId}/subject/view/${subjectId}`
  },
}

// Decode the url params into the route to land on after login, or null when they don't
// describe a view we know.
const decodeRedirectTarget = (params: URLSearchParams): string | null => {
  const view = params.get('view')
  const buildPath = view ? PATH_BY_VIEW[view] : null
  return buildPath ? buildPath(params) : null
}

// Open-redirect guard: only internal absolute paths (a single leading slash).
const isInternalPath = (value: string | null): value is string => {
  return !!value && value.startsWith('/') && !value.startsWith('//')
}

// Remember where to land after login, from the notification url params.
export const savePostLoginRedirect = (search: string): void => {
  try {
    const target = decodeRedirectTarget(new URLSearchParams(search))
    if (isInternalPath(target)) {
      sessionStorage.setItem(KEY, target)
    }
  } catch {}
}

// Where to go once logged in - read once, then clear.
export const consumePostLoginRedirect = (): string | null => {
  try {
    const target = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    return isInternalPath(target) ? target : null
  } catch {
    return null
  }
}
